// The registration form itself — captain per team.
//
// The shape of this form is decided by how outings are actually sold, not by what a
// database row happens to have columns for. A sponsor buys a foursome in March; the four
// people who will actually play are settled in the car park on the morning of the event.
// So the ONLY required field here is the team name, which is very often the sponsor's
// name rather than anything about golf ("Gurnee Ford"). Every player name is optional and
// a team arriving with zero of them is the ordinary case, not an error to be caught.
//
// No handicap field, on purpose: charity outings play gross and most of the field has no
// index to give. No payment step either — the course collects entry fees the way it
// already does, and 4sum never touches that money.

import { useState, type CSSProperties, type FormEvent } from 'react';
import { supabase, errText } from '../lib/supabase';

/** The event, as `join_tournament` describes it. */
export interface Tournament {
  id?: string;
  name: string;
  starts_on: string | null;
  course_name: string | null;
  team_size: number | null;
  format: string | null;
  status: string | null;
  scoring?: string | null;
  // Whether this link is still taking teams, and how full the field is. The server enforces
  // all three on submit regardless; the page reads them only so a captain is not asked to
  // fill in a form that cannot be accepted. Optional because an older deployment of the RPC
  // will not send them, and a field that is absent must not read as "closed".
  signups_open?: boolean | null;
  max_teams?: number | null;
  teams_taken?: number | null;
}

export interface SignupResult {
  teamName: string;
  namesGiven: number;
  seats: number;
}

/** Seats to show, clamped to what the schema actually allows. */
export function seatCount(t: Tournament): number {
  const n = Number(t.team_size);
  if (!Number.isFinite(n) || n < 1) return 4;
  return Math.min(Math.max(Math.trunc(n), 1), 8);
}

const labelRow: CSSProperties = {
  display: 'flex',
  gap: 12,
  flexWrap: 'wrap',
};
const half: CSSProperties = { flex: '1 1 220px', minWidth: 0 };

export default function SignupForm({
  code,
  tournament,
  onRegistered,
}: {
  code: string;
  tournament: Tournament;
  onRegistered: (result: SignupResult) => void;
}) {
  const seats = seatCount(tournament);

  const [teamName, setTeamName] = useState('');
  const [captainName, setCaptainName] = useState('');
  const [captainEmail, setCaptainEmail] = useState('');
  const [captainPhone, setCaptainPhone] = useState('');
  const [players, setPlayers] = useState<string[]>(() => Array(seats).fill(''));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touchedName, setTouchedName] = useState(false);

  const nameMissing = teamName.trim().length === 0;

  function setPlayer(i: number, value: string) {
    setPlayers((prev) => {
      const next = prev.slice();
      next[i] = value;
      return next;
    });
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setTouchedName(true);
    if (nameMissing) {
      setError('A foursome needs a name');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      // Send the full set of seats even when most are blank. The server opens every seat
      // regardless, so the host has somewhere to put the names when they turn up.
      const playerNames = players.slice(0, seats).map((p) => p.trim());
      const { error: rpcError } = await supabase.rpc('signup_team', {
        p_join_code: code,
        p_team_name: teamName.trim(),
        p_captain_name: captainName.trim() || null,
        p_captain_email: captainEmail.trim() || null,
        p_captain_phone: captainPhone.trim() || null,
        p_player_names: playerNames,
      });
      if (rpcError) {
        // Server messages here are written for a human being standing at a first tee
        // ("Signups for the Fall Classic are closed", "The field for X is full"). Showing
        // them verbatim is the whole point — a generic "something went wrong" would strand
        // the captain with no idea whether to try again or ring the pro shop.
        setError(errText(rpcError));
        return;
      }
      onRegistered({
        teamName: teamName.trim(),
        namesGiven: playerNames.filter(Boolean).length,
        seats,
      });
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card" onSubmit={submit} noValidate style={{ marginTop: 16 }}>
      <h2 style={{ marginBottom: 4 }}>Register your foursome</h2>
      <p style={{ margin: '0 0 16px', color: 'var(--ink-dim)', fontSize: 14 }}>
        Only the foursome name is required. Everything else can wait.
      </p>

      <div className="field">
        <label htmlFor="team-name">Foursome name (required)</label>
        <input
          id="team-name"
          value={teamName}
          onChange={(e) => setTeamName(e.target.value)}
          onBlur={() => setTouchedName(true)}
          maxLength={80}
          autoComplete="organization"
          autoCapitalize="words"
          placeholder="Gurnee Ford"
          aria-invalid={touchedName && nameMissing}
        />
        <p style={{ margin: '4px 0 0', color: 'var(--ink-dim)', fontSize: 13 }}>
          Sponsor name, company, or whatever your foursome goes by.
        </p>
        {touchedName && nameMissing ? (
          <p className="err" style={{ margin: '4px 0 0', fontSize: 13 }}>
            A foursome needs a name
          </p>
        ) : null}
      </div>

      <hr
        style={{
          border: 0,
          borderTop: '1px solid var(--paper-border)',
          margin: '20px 0 16px',
        }}
      />

      <h3 style={{ marginBottom: 2 }}>Who should we contact?</h3>
      <p style={{ margin: '0 0 12px', color: 'var(--ink-dim)', fontSize: 13 }}>
        Optional — this is how the host reaches you about tee times and changes.
      </p>

      <div className="field">
        <label htmlFor="captain-name">Your name</label>
        <input
          id="captain-name"
          value={captainName}
          onChange={(e) => setCaptainName(e.target.value)}
          autoComplete="name"
          autoCapitalize="words"
        />
      </div>

      <div style={labelRow}>
        <div className="field" style={half}>
          <label htmlFor="captain-email">Email</label>
          <input
            id="captain-email"
            type="email"
            inputMode="email"
            value={captainEmail}
            onChange={(e) => setCaptainEmail(e.target.value)}
            autoComplete="email"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
          />
        </div>
        <div className="field" style={half}>
          <label htmlFor="captain-phone">Phone</label>
          <input
            id="captain-phone"
            type="tel"
            inputMode="tel"
            value={captainPhone}
            onChange={(e) => setCaptainPhone(e.target.value)}
            autoComplete="tel"
          />
        </div>
      </div>

      <hr
        style={{
          border: 0,
          borderTop: '1px solid var(--paper-border)',
          margin: '20px 0 16px',
        }}
      />

      <h3 style={{ marginBottom: 2 }}>Players</h3>
      <p style={{ margin: '0 0 12px', color: 'var(--ink-dim)', fontSize: 13 }}>
        All optional. Leave them blank if you do not know yet — most captains do not, and
        the host can add names right up to the first tee.
      </p>

      {players.slice(0, seats).map((value, i) => (
        <div className="field" key={i}>
          <label htmlFor={`player-${i}`}>Player {i + 1}</label>
          <input
            id={`player-${i}`}
            value={value}
            onChange={(e) => setPlayer(i, e.target.value)}
            autoComplete="off"
            autoCapitalize="words"
            placeholder="—"
          />
        </div>
      ))}

      {error ? (
        <p className="err" style={{ margin: '16px 0 0' }} role="alert">
          {error}
        </p>
      ) : null}

      <div style={{ marginTop: 20 }}>
        <button type="submit" disabled={busy} style={{ width: '100%', padding: 14 }}>
          {busy ? 'Registering…' : 'Register foursome'}
        </button>
        <p style={{ margin: '10px 0 0', color: 'var(--ink-dim)', fontSize: 13 }}>
          No payment here — the course handles entry fees the way it always has.
        </p>
      </div>
    </form>
  );
}
